import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { InfoIcon } from '@/app/components/icons/InfoIcon';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { StripeConfirmDialog } from '@/app/components/shared/Pay/components/StripeConfirmDialog';
import { Button } from '@/app/components/ui/button';
import { SITE } from '@/app/constants';
import { usePayment } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ClawPassProduct } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { ClawAdvancePurchaseInfo } from './ClawAdvancePurchaseInfo';

const AvailableButton = ({
  onClick,
  product,
  isAdvancePurchase,
  showAdvanceHint,
}: {
  onClick: () => void;
  product: ClawPassProduct;
  isAdvancePurchase: boolean;
  showAdvanceHint: boolean;
}) => {
  const t = useTranslations();
  const button = (
    <Button
      className={cn(
        'group relative h-12 w-full rounded-12 border-none bg-white font-medium text-black text-sm',
        product.claw_pass_plan_type === 'claw_pass_pro' &&
          'bg-center bg-cover bg-no-repeat text-white shadow-[inset_0_0_10px_0_rgba(255,255,255,0.16)]',
      )}
      onClick={() => {
        reportEvent('claw_vip_price_click', {
          subscribe_type: `${product.product_type}-${product.billing_period}`,
          vip_type: product.claw_pass_plan_type || '',
          is_advance_purchase: isAdvancePurchase,
        });
        onClick();
      }}
      style={
        product.claw_pass_plan_type === 'claw_pass_pro'
          ? {
              backgroundImage:
                SITE === 'china'
                  ? 'url(https://image01.vidu.zone/vidu/media-asset/claw_pass-d64585a6.webp)'
                  : 'url(https://image01.cf.vidu.studio/vidu/media-asset/claw_pass-d64585a6.webp)',
            }
          : undefined
      }
    >
      {isAdvancePurchase ? t('payments.plans.passAdvanceButton') : t('payments.plans.passButton')}
      {showAdvanceHint && <InfoIcon className="ml-2 size-4 shrink-0 opacity-60" />}
    </Button>
  );

  if (showAdvanceHint) {
    return (
      <CustomTooltip
        content={t('payments.plans.clawAdvancePurchaseHint')}
        side="bottom"
        trigger="auto"
        disableHoverableContent={true}
      >
        {button}
      </CustomTooltip>
    );
  }

  return button;
};

const UnavailableButton = ({
  hasActiveClawPass,
  isClawPassUserBuyingBundle,
}: {
  hasActiveClawPass: boolean;
  isClawPassUserBuyingBundle: boolean;
}) => {
  const t = useTranslations();
  // 权益包用户（会员过期）想买捆绑套餐：提示先买 Vidu 会员
  // 已有 Claw 权益生效：无法升级 / 叠加购买
  // 否则视为「该套餐仅限购买一次」的次数限制
  const tooltipContent = isClawPassUserBuyingBundle
    ? t('payments.plans.clawPassUnavailableForMemberExpired')
    : hasActiveClawPass
      ? t('payments.plans.clawPassUnavailableActive')
      : t('payments.plans.clawPassUnavailableLimit');

  return (
    <CustomTooltip side="bottom" content={tooltipContent} trigger="auto" disableHoverableContent={true}>
      <Button className="h-12 w-full rounded-12 bg-system-white12 text-sm text-system-white36 backdrop-blur-md">
        {t('payments.plans.passButton')}
        <InfoIcon className="ml-2 h-4 w-4 shrink-0" />
      </Button>
    </CustomTooltip>
  );
};

export const SubscriptionButton = ({ product, quantity = 1 }: { product: ClawPassProduct; quantity?: number }) => {
  const { pay } = usePayment();
  const clawPass = useStore((state) => state.clawPass);
  const userInfo = useStore((state) => state.userInfo);
  const userVariant = clawPass?.userVariant;
  const [showStripeConfirm, setShowStripeConfirm] = useState(false);

  const isAdvancePurchase = clawPass?.has_benefit === true && userVariant !== 'trial';
  const isStripe = product.pay_channel === 'stripe';
  const hasCoupons = (product.available_coupons?.length ?? 0) > 0;
  // Bundle 用户买 Pass：本次购买不含会员权益，需在确认弹窗里告知
  const isCrossToPass = clawPass?.product_type === 'claw_pass_bundle' && product.product_type === 'claw_pass';
  const needsExtraInfo = isAdvancePurchase || isCrossToPass;

  const handleClickPayment = () => {
    // Stripe 直接跳转支付，缺少确认环节：续购、cross-buy 或有可用券时，先弹确认框
    // （国内支付宝在收银台已有等价确认，无需此弹窗）
    if (isStripe && (needsExtraInfo || hasCoupons)) {
      setShowStripeConfirm(true);
      return;
    }
    pay(product, { quantity });
  };

  // 权益包用户（会员过期：无 vidu 会员订阅但有 claw 权益）想购买捆绑套餐
  // 覆盖两种权益包场景：claw_pass（普通权益包）和 claw_pass_bundle 到期后续持情形
  const isClawPassUserBuyingBundle =
    clawPass?.has_benefit === true &&
    (clawPass?.product_type === 'claw_pass' || clawPass?.product_type === 'claw_pass_bundle') &&
    product.product_type === 'claw_pass_bundle' &&
    userInfo?.subs_plan === 'free';

  const renderButton = () => {
    if (product.product_pay_status === 'unavailable') {
      return (
        <UnavailableButton
          hasActiveClawPass={clawPass?.has_benefit === true}
          isClawPassUserBuyingBundle={isClawPassUserBuyingBundle}
        />
      );
    }
    return (
      <AvailableButton
        onClick={handleClickPayment}
        product={product}
        isAdvancePurchase={isAdvancePurchase}
        showAdvanceHint={userVariant === 'clawPass'}
      />
    );
  };

  return (
    <>
      {renderButton()}
      {showStripeConfirm && (
        <StripeConfirmDialog
          product={product}
          open={showStripeConfirm}
          quantity={quantity}
          extraInfo={needsExtraInfo ? <ClawAdvancePurchaseInfo product={product} quantity={quantity} /> : undefined}
          onClose={() => setShowStripeConfirm(false)}
          onConfirm={(couponId) => {
            setShowStripeConfirm(false);
            pay(product, { couponIdOverride: couponId, quantity });
            reportEvent('vip_price_click', {
              subscribe_type: `${product.product_type}-${product.billing_period}`,
              vip_type: product.claw_pass_plan_type || '',
            });
          }}
        />
      )}
    </>
  );
};
